import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Download, Monitor, Smartphone, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTeam } from "@/lib/team";
import { enablePush, pushSupported } from "@/lib/push";
import { desktopBrowser, getInstallPrompt, isDesktop, isStandalone, onInstallPrompt, reportDevice, runInstall } from "@/lib/installPrompt";
import InstallAnimation from "@/pages/team/InstallAnimation";
import PhoneInstallAnimation from "@/pages/team/PhoneInstallAnimation";

const seenKey = (email) => `snaplist.pcInstallSeen.${(email || "").toLowerCase()}`;
const tourKey = (email) => `snaplist.vaTour.${(email || "").toLowerCase()}`;
const ALERTS_ASKED = "snaplist.pcAlertsAsked";
export const openPcInstall = () => window.dispatchEvent(new Event("snaplist:pc-install"));

const STEPS = {
  chrome: ["Look at the right end of the address bar", "Click the install icon (a little screen with a down arrow)", "Click Install. SnapList opens in its own window and gets a taskbar / Dock icon"],
  edge: ["Look at the right end of the address bar", "Click the App available icon (squares with a plus)", "Click Install. Pin it to the taskbar when Edge asks"],
  safari: ["Click File in the menu bar at the top of the screen", "Click Add to Dock…", "Click Add. SnapList now opens from your Dock like an app"],
  iphone: ["In Safari, tap Share (the square with an arrow) at the bottom", "Scroll down and tap Add to Home Screen", "Tap Add, then open SnapList from the new icon"],
  android: ["In Chrome, tap the ⋮ menu at the top right", "Tap Install app (or Add to Home screen)", "Tap Install, then open SnapList from the new icon"],
};
const NAMES = { chrome: "Chrome", edge: "Edge", safari: "Safari on Mac", iphone: "iPhone", android: "Android" };

export const canOfferInstall = () => !isStandalone() && localStorage.getItem("snaplist.pcInstalled") !== "1";
const firstTab = (pc) => (pc ? (["chrome", "edge", "safari"].includes(desktopBrowser()) ? desktopBrowser() : "chrome") : /iphone|ipad|ipod/i.test(navigator.userAgent) ? "iphone" : "android");

function AlertsAsk({ onDone }) {
  const turnOn = async () => {
    const r = await enablePush().catch(() => "error");
    localStorage.setItem(ALERTS_ASKED, "1");
    reportDevice();
    if (r === "granted") toast.success("Desktop alerts are on. A test alert is on its way.");
    else toast.message("Alerts are off. You can turn them on later from the bell icon.");
    onDone();
  };
  return (
    <div className="flex items-start gap-3 rounded-xl border border-[#0053DA]/20 bg-[#0053DA]/5 p-4" data-testid="pc-alerts-ask">
      <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-[#0053DA]" />
      <div className="flex-1">
        <p className="text-sm font-semibold text-slate-900">Turn on free desktop alerts</p>
        <p className="mt-0.5 text-xs text-slate-600">Get a pop-up on this computer for new photos to list, questions and answers, even when SnapList is closed.</p>
        <button type="button" onClick={turnOn} className="mt-2 rounded-full bg-[#0053DA] px-4 py-1.5 text-xs font-semibold text-white" data-testid="pc-alerts-enable">Turn on alerts</button>
      </div>
    </div>
  );
}

// Team members: animated "install SnapList" guide (PC or phone), once after the VA tour; reopen from the header. Then desktop alerts.
export default function PcInstallGuide() {
  const { user } = useAuth();
  const { isAssistant } = useTeam();
  const pc = isDesktop();
  const [open, setOpen] = useState(false);
  const [alertsOnly, setAlertsOnly] = useState(false);
  const [askAlerts, setAskAlerts] = useState(false);
  const [prompt, setPrompt] = useState(getInstallPrompt);
  const [tab, setTab] = useState(() => firstTab(pc));

  useEffect(() => onInstallPrompt(setPrompt), []);
  useEffect(() => {
    if (!isAssistant || !user?.email) return undefined;
    reportDevice();
    window.__snaplistReport = () => reportDevice();
    const needAlerts = () => pc && isStandalone() && pushSupported() && Notification.permission === "default" && !localStorage.getItem(ALERTS_ASKED);
    const first = () => {
      if (needAlerts()) { setAlertsOnly(true); setOpen(true); }
      else if (canOfferInstall() && !localStorage.getItem(seenKey(user.email))) setOpen(true);
    };
    if (localStorage.getItem(tourKey(user.email))) first();
    const replay = () => { setAlertsOnly(false); setAskAlerts(false); setOpen(true); };
    window.addEventListener("snaplist:va-tour-done", first);
    window.addEventListener("snaplist:pc-install", replay);
    return () => { window.removeEventListener("snaplist:va-tour-done", first); window.removeEventListener("snaplist:pc-install", replay); };
  }, [isAssistant, user?.email, pc]);

  if (!open) return null;
  const close = () => { localStorage.setItem(seenKey(user?.email), "1"); setOpen(false); };
  const install = async () => {
    const r = await runInstall();
    if (r !== "accepted") return;
    reportDevice(true);
    if (!pc || !pushSupported() || Notification.permission !== "default") { toast.success("Installed! Open SnapList from its new icon."); return close(); }
    // Ask for desktop alerts right away; if the browser wants a fresh tap, show the button.
    const perm = await enablePush().catch(() => "error");
    localStorage.setItem(ALERTS_ASKED, "1");
    reportDevice(true);
    if (perm === "granted") { toast.success("Installed, and desktop alerts are on."); return close(); }
    if (Notification.permission === "default") return setAskAlerts(true);
    toast.success("Installed! Open SnapList from your taskbar or Dock.");
    close();
  };
  const tabs = pc ? ["chrome", "edge", "safari"] : ["iphone", "android"];
  const native = prompt && !["safari", "iphone"].includes(tab);
  const TitleIcon = pc ? Monitor : Smartphone;

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-950/70 p-3 backdrop-blur-sm sm:items-center sm:p-4" data-testid="pc-install-guide">
      <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-5">
          <div>
            <p className="flex items-center gap-2 text-lg font-bold text-slate-900"><TitleIcon className="h-5 w-5 text-[#0053DA]" />
              {alertsOnly ? "One last step: desktop alerts" : pc ? "Install SnapList on this computer" : "Add SnapList to your home screen"}</p>
            {!alertsOnly && <p className="mt-0.5 text-sm text-slate-600">{pc ? "Opens in its own window with a taskbar / Dock icon, so your work queue is one click away." : "Opens full screen like an app, one tap away, with free alerts for new work."}</p>}
          </div>
          <button type="button" onClick={() => { if (alertsOnly) localStorage.setItem(ALERTS_ASKED, "1"); close(); }} aria-label="Close" className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100" data-testid="pc-install-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5">
          {alertsOnly || askAlerts ? <AlertsAsk onDone={close} /> : (
            <>
              <div className="mb-3 flex gap-1.5" role="tablist" data-testid="pc-install-browsers">
                {tabs.map((b) => (
                  <button key={b} type="button" role="tab" aria-selected={tab === b} onClick={() => setTab(b)} data-testid={`pc-install-tab-${b}`}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${tab === b ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                    {NAMES[b]}
                  </button>
                ))}
              </div>
              {pc ? <InstallAnimation browser={tab} key={tab} /> : <PhoneInstallAnimation os={tab} key={tab} />}
              <ol className="mt-4 space-y-2" data-testid="pc-install-steps">
                {STEPS[tab].map((s, i) => (
                  <li key={s} className="pci-step flex gap-3 text-sm text-slate-700" style={{ animationDelay: `${i * 2}s` }}>
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0053DA]/10 text-xs font-bold text-[#0053DA]">{i + 1}</span>{s}
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
        {!alertsOnly && !askAlerts && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 p-4">
            <button type="button" onClick={close} className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200" data-testid="pc-install-later">Maybe later</button>
            {native ? (
              <button type="button" onClick={install} className="inline-flex items-center gap-2 rounded-full bg-[#0053DA] px-5 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" data-testid="pc-install-now">
                <Download className="h-4 w-4" /> Install now
              </button>
            ) : (
              <button type="button" onClick={close} className="rounded-full bg-[#0053DA] px-5 py-2 text-sm font-semibold text-white" data-testid="pc-install-done">Got it</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
